"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import "swiper/css";
import {
  Truck,
  BookOpen,
  Headphones,
  LayoutGrid,
  Minus,
  Plus,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Heart,
  Star,
  Gavel,
  Store,
} from "lucide-react";
import { MessageSquare } from "lucide-react";
import { Avatar, Image as AntImage } from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import { useCartStore } from "context/cartStore";
import { useAuthUser } from "hooks/useAuth";
import { useWishStore } from "context/wishStore";
import axios from "axios-base";
import base from "lib/base";
import { getStoreSettings } from "lib/store-settings";
import { getDeliveryFeeText, getDeliveryInfo } from "lib/delivery-info";
import { calculateDeliveryPricing } from "lib/quantity-discount";
import styles from "./BookDetails.module.css";
import YouTubeCard from "./YouTubeCard";
import UnifiedAuthorModal from "./AuthorModal";
import newsGridStyles from "./NewsGrid.module.css";
import { toast } from "sonner";
import SafeHTML from "./SafeHTML";
import ShareSection from "./ShareSection";

const TAB_ITEMS = [
  { key: "details", label: "Дэлгэрэнгүй" },
  { key: "chapters", label: "Бүлгүүд" },
  { key: "reviews", label: "Сэтгэгдэл" },
];

export default function BookDetails({
  book,
  videos = [],
  relatedNews = [],
  appInfo = null,
  detailUrl = "",
  shareTitle = "",
}) {
  const { addToCart } = useCartStore();
  const user = useAuthUser();
  const { toggleWishlist, isWishlisted } = useWishStore();
  const router = useRouter();

  const [mounted, setMounted] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("delivery");
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("details");
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [storeSettings, setStoreSettings] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showBookstoreModal, setShowBookstoreModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewPagination, setReviewPagination] = useState({
    total: 0,
    pages: 0,
    limit: 5,
  });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitError, setReviewSubmitError] = useState("");
  const [ratingSummary, setRatingSummary] = useState(null);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [isChapterScrollEnd, setIsChapterScrollEnd] = useState(false);

  const appInfoItem = Array.isArray(appInfo) ? appInfo[0] : appInfo || null;
  const bookId = book?._id || book?.id;
  const userId = user?._id || user?.id;
  const chapterItems = Array.isArray(book.topics) ? book.topics : [];
  const hasChapters = chapterItems.length > 0;

  useEffect(() => {
    const safeSlug = encodeURIComponent(String(book?.slug || "").trim());
    const candidates = [
      `${base.apiUrl}/gerelt_books/slug/${safeSlug}`,
      `${base.apiUrl}/gerelt_books/s/${safeSlug}`,
      `${base.apiUrl}/gerelt_books?slug=${safeSlug}&limit=1`,
    ];
    console.info("[BookDetails][Client] Candidate endpoints:", candidates);
  }, [book?.slug]);

  // Prevent hydration mismatch by only reading wishlist state after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    getStoreSettings().then(setStoreSettings);
  }, []);

  useEffect(() => {
    getDeliveryInfo().then(setDeliveryInfo);
  }, []);

  const fetchReviews = async (page = 1, replace = false) => {
    if (!bookId) return;
    setReviewsLoading(true);
    setReviewsError("");
    try {
      const endpoint = `gerelt/books/${bookId}/reviews?limit=${reviewPagination.limit}&page=${page}`;
      console.info("[BookDetails][Client] Fetching endpoint:", `${base.apiUrl}/${endpoint}`);
      const res = await axios.get(
        endpoint,
      );
      const items = res?.data?.data || [];
      const pagination = res?.data?.pagination || {};
      setReviewPagination((prev) => ({
        total: pagination.total ?? prev.total,
        pages: pagination.pages ?? prev.pages,
        limit: pagination.limit ?? prev.limit,
      }));
      setReviewPage(page);
      setReviews((prev) => (replace ? items : [...prev, ...items]));
    } catch (error) {
      setReviewsError("Сэтгэгдэл ачаалж чадсангүй.");
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchRatingSummary = async () => {
    if (!bookId) return;
    try {
      const endpoint = `gerelt/books/${bookId}/rating`;
      console.info("[BookDetails][Client] Fetching endpoint:", `${base.apiUrl}/${endpoint}`);
      const res = await axios.get(endpoint);
      const data = res?.data?.data || null;
      if (data) {
        setRatingSummary({
          avg: Number(data.avg || 0),
          count: Number(data.count || 0),
        });
      }
    } catch (error) {
      // no-op
    }
  };

  useEffect(() => {
    if (!bookId) return;
    fetchReviews(1, true);
    fetchRatingSummary();
  }, [bookId]);

  useEffect(() => {
    if (!userId) {
      setReviewForm({ rating: 0, comment: "" });
    }
  }, [userId]);

  useEffect(() => {
    if (activeTab === "chapters") {
      setIsChapterScrollEnd(false);
    }
  }, [activeTab, chapterItems.length]);

  // Build images array from all available sources
  const allImages = [
    book.picture,
    ...(book.images || []),
    ...(book.pictures || []),
  ].filter(Boolean);

  const images =
    allImages.length > 0 ? allImages : book.cover ? [book.cover] : [];
  const selectedImage = images[selectedIndex] || null;

  useEffect(() => {
    setSelectedIndex(0);
  }, [book?._id]);

  useEffect(() => {
    if (selectedIndex >= images.length) setSelectedIndex(0);
  }, [images.length, selectedIndex]);

  const getPrice = () => {
    if (selectedFormat === "delivery") return book.price?.delivery?.price || 0;
    if (selectedFormat === "epub") return book.price?.epub?.price || 0;
    if (selectedFormat === "audio") return book.price?.audio?.price || 0;
    return 0;
  };

  const currentPrice = getPrice();
  const discount =
    selectedFormat === "delivery" ? book.price?.delivery?.discount || 0 : 0;
  const quantityDiscountRules =
    book.price?.delivery?.quantityDiscountRules || [];
  const deliveryPricing = calculateDeliveryPricing({
    price: currentPrice,
    discount,
    quantity,
    quantityDiscountRules,
  });
  const totalBeforeDiscounts = currentPrice * quantity;
  const totalDiscount = Math.max(
    0,
    totalBeforeDiscounts - deliveryPricing.finalTotal,
  );
  const finalPrice =
    selectedFormat === "delivery"
      ? deliveryPricing.finalUnitPrice
      : currentPrice;
  const stockQty = book.price?.delivery?.quantity || 0;
  const formatDetails =
    selectedFormat === "delivery"
      ? book.price?.delivery?.details || ""
      : selectedFormat === "epub"
        ? book.price?.epub?.details || ""
        : selectedFormat === "audio"
          ? book.price?.audio?.details || ""
          : "";
  const hasFormatDetails =
    typeof formatDetails === "string" &&
    formatDetails
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, "")
      .trim().length > 0;
  const displayRatingAvg = Number(ratingSummary?.avg ?? book.ratingAvg ?? 0);
  const displayRatingCount = Number(
    ratingSummary?.count ?? book.ratingCount ?? 0,
  );
  const findUserRatedReview = (items) => {
    for (const item of items) {
      const reviewUserId = item?.user?._id || item?.user?.id || null;
      if (reviewUserId === userId && Number(item?.rating || 0) > 0) {
        return item;
      }
      if (Array.isArray(item?.replies) && item.replies.length > 0) {
        const nested = findUserRatedReview(item.replies);
        if (nested) return nested;
      }
    }
    return null;
  };

  const currentUserRatedReview = userId ? findUserRatedReview(reviews) : null;
  const hasUserRated = Boolean(currentUserRatedReview);

  const handleAddToCart = async () => {
    await addToCart(book, quantity, user);
  };

  const handleBuyNow = async () => {
    // Add to cart first
    await addToCart(book, quantity, user);
    // Close cart drawer immediately (addToCart opens it by default)
    useCartStore.getState().setIsOpen(false);
    // Then navigate directly to checkout
    router.push("/checkout");
  };

  const handleToggleWish = () => {
    toggleWishlist(book, user);
  };

  const handleReviewRatingSelect = (value) => {
    setReviewForm((prev) => ({ ...prev, rating: value }));
  };

  const handleReviewCommentChange = (event) => {
    setReviewForm((prev) => ({ ...prev, comment: event.target.value }));
  };

  const handleDeleteReview = async (reviewId) => {
    if (!reviewId) return;
    const ok = window.confirm("Сэтгэгдлээ устгах уу?");
    if (!ok) return;

    setReviewSubmitting(true);
    setReviewSubmitError("");
    try {
      const res = await axios.delete(`gerelt/reviews/${reviewId}`);
      const summary = res?.data?.rating;
      if (summary) {
        setRatingSummary({
          avg: Number(summary.avg || 0),
          count: Number(summary.count || 0),
        });
      }
      setReviews((prev) => removeReviewFromTree(prev, reviewId));
    } catch (error) {
      setReviewSubmitError("Сэтгэгдэл устгахад алдаа гарлаа.");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!userId) return;
    const normalizedComment = String(reviewForm.comment || "").trim();
    const selectedRating = Number(reviewForm.rating || 0);
    const shouldSendRating = !hasUserRated && selectedRating > 0;

    if (!shouldSendRating && !normalizedComment) {
      setReviewSubmitError("Сэтгэгдэл эсвэл үнэлгээ оруулна уу.");
      return;
    }

    setReviewSubmitting(true);
    setReviewSubmitError("");
    try {
      const res = await axios.post(`gerelt/books/${bookId}/reviews`, {
        ...(shouldSendRating ? { rating: selectedRating } : {}),
        comment: normalizedComment,
      });
      const created = res?.data?.data;
      const summary = res?.data?.rating;

      if (summary) {
        setRatingSummary({
          avg: Number(summary.avg || 0),
          count: Number(summary.count || 0),
        });
      }

      if (created) {
        const createdId = created._id || created.id;
        const parentId = created?.parentReview || null;
        setReviews((prev) => {
          if (parentId) {
            return appendReplyToTree(prev, parentId, created);
          }
          const idx = prev.findIndex((item) => item._id === createdId);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...prev[idx], ...created };
            return next;
          }
          return [created, ...prev];
        });
        setReviewForm((prev) => ({
          rating: shouldSendRating ? 0 : prev.rating,
          comment: "",
        }));
        setReplyingToId(null);
        setReplyText("");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || "Сэтгэгдэл илгээхэд алдаа гарлаа.";
      setReviewSubmitError(message);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleLoadMoreReviews = () => {
    if (reviewsLoading) return;
    const nextPage = reviewPage + 1;
    if (reviewPagination.pages && nextPage > reviewPagination.pages) return;
    fetchReviews(nextPage, false);
  };

  const handleSubmitReply = async (parentReviewId) => {
    if (!userId || !parentReviewId) return;
    const normalizedComment = String(replyText || "").trim();
    if (!normalizedComment) {
      setReviewSubmitError("Reply сэтгэгдлээ бичнэ үү.");
      return;
    }

    setReviewSubmitting(true);
    setReviewSubmitError("");
    try {
      const res = await axios.post(`gerelt/books/${bookId}/reviews`, {
        parentReview: parentReviewId,
        comment: normalizedComment,
      });

      const created = res?.data?.data;
      const summary = res?.data?.rating;
      if (summary) {
        setRatingSummary({
          avg: Number(summary.avg || 0),
          count: Number(summary.count || 0),
        });
      }

      if (created) {
        setReviews((prev) => appendReplyToTree(prev, parentReviewId, created));
      }
      setReplyText("");
      setReplyingToId(null);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Reply илгээхэд алдаа гарлаа.";
      setReviewSubmitError(message);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handlePrevImage = () => {
    if (images.length <= 1) return;
    setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = () => {
    if (images.length <= 1) return;
    setSelectedIndex((prev) => (prev + 1) % images.length);
  };

  const handleOpenStore = (store) => {
    setSelectedStore(store);
    setShowBookstoreModal(true);
  };

  const handleChapterScroll = (event) => {
    const element = event.currentTarget;
    const isAtEnd =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 4;
    setIsChapterScrollEnd(isAtEnd);
  };

  const renderReviewItem = (review, depth = 0) => {
    const reviewId = review?._id || review?.id;
    const reviewUserId = review?.user?._id || review?.user?.id || null;
    const isOwner = userId && reviewUserId === userId;
    const hasRating = Number(review?.rating || 0) > 0;
    const isReplying = replyingToId === reviewId;
    const replies = Array.isArray(review?.replies) ? review.replies : [];

    return (
      <div
        key={reviewId}
        className={styles.reviewItem}
        style={
          depth > 0
            ? { marginLeft: `${Math.min(depth * 20, 60)}px` }
            : undefined
        }
      >
        <div className={styles.reviewHeader}>
          <Avatar src={resolveUrl(review?.user?.avatar)} size={36}>
            {review?.user?.name?.[0]}
          </Avatar>
          <div className={styles.reviewMeta}>
            <div className={styles.reviewAuthor}>
              {review?.user?.name || "Хэрэглэгч"}
            </div>
            <div className={styles.reviewDate}>
              {formatDate(review?.createdAt)}
            </div>
          </div>
          <div className={styles.reviewStars}>
            {hasRating ? (
              [1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={16}
                  className={styles.reviewStar}
                  fill={star <= (review?.rating || 0) ? "#ffa500" : "none"}
                  stroke="#ffa500"
                />
              ))
            ) : (
              <span className={styles.commentOnlyBadge}>Сэтгэгдэл</span>
            )}
          </div>
          <div className={styles.reviewActions}>
            {userId && (
              <button
                type="button"
                className={styles.reviewActionBtn}
                onClick={() => {
                  setReplyingToId(reviewId);
                  setReplyText("");
                }}
                aria-label="Reply бичих"
              >
                <MessageSquare size={13} />
                Хариулах
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                className={`${styles.reviewActionBtn} ${styles.reviewActionDanger}`}
                onClick={() => handleDeleteReview(reviewId)}
                aria-label="Сэтгэгдэл устгах"
                disabled={reviewSubmitting}
              >
                <Gavel size={14} />
                Устгах
              </button>
            )}
          </div>
        </div>
        {review?.comment && (
          <div className={styles.reviewComment}>{review.comment}</div>
        )}

        {isReplying && userId && (
          <div className={styles.replyComposer}>
            <textarea
              className={styles.reviewTextarea}
              rows={3}
              placeholder="Reply сэтгэгдлээ бичнэ үү"
              value={replyText}
              onChange={(event) => setReplyText(event.target.value)}
            />
            <div className={styles.replyComposerActions}>
              <button
                type="button"
                className={styles.submitReviewBtn}
                onClick={() => handleSubmitReply(reviewId)}
                disabled={reviewSubmitting}
              >
                {reviewSubmitting ? "Илгээж байна..." : "Reply илгээх"}
              </button>
              <button
                type="button"
                className={styles.replyCancelBtn}
                onClick={() => {
                  setReplyingToId(null);
                  setReplyText("");
                }}
              >
                Болих
              </button>
            </div>
          </div>
        )}

        {replies.length > 0 && (
          <div className={styles.replyList}>
            {replies.map((item) => renderReviewItem(item, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className="row">
        {/* Left Column: Images */}
        <div className="col-lg-5 mb-4">
          <div className={styles.imageSection}>
            <div className={styles.mainImageWrapper}>
              {selectedImage ? (
                <AntImage.PreviewGroup
                  items={images.map((img) => resolveUrl(img))}
                  preview={{
                    visible: isViewerOpen,
                    onVisibleChange: (visible) => setIsViewerOpen(visible),
                    current: selectedIndex,
                    onChange: (index) => setSelectedIndex(index),
                  }}
                >
                  <AntImage
                    key={selectedImage}
                    src={resolveUrl(selectedImage)}
                    alt={book.name}
                    className={`${styles.mainImage} ${styles.mainImageFade}`}
                    preview={false}
                    onClick={() => setIsViewerOpen(true)}
                  />
                </AntImage.PreviewGroup>
              ) : (
                <div className={styles.placeholderImage}>Зураггүй</div>
              )}
              {images.length > 1 && (
                <>
                  <button
                    className={`${styles.imageNavBtn} ${styles.imageNavLeft}`}
                    onClick={handlePrevImage}
                    aria-label="Өмнөх зураг"
                    type="button"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    className={`${styles.imageNavBtn} ${styles.imageNavRight}`}
                    onClick={handleNextImage}
                    aria-label="Дараагийн зураг"
                    type="button"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
              <button
                className={`${styles.wishBtn} ${mounted && isWishlisted(book?._id) ? styles.wishActive : ""}`}
                onClick={handleToggleWish}
                aria-pressed={mounted ? isWishlisted(book?._id) : false}
                aria-label="Хадгалах"
                type="button"
              >
                <Heart
                  size={20}
                  fill={mounted && isWishlisted(book?._id) ? "#ff4d4f" : "none"}
                />
              </button>
            </div>

            {images.length > 1 && (
              <div className={styles.thumbnailList}>
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className={`${styles.thumbnail} ${selectedIndex === idx ? styles.activeThumbnail : ""}`}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    <img src={resolveUrl(img)} alt={`Thumbnail ${idx}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Info & Actions */}
        <div className="col-lg-7">
          <h1 className={styles.bookTitle}>{book.name || book.title}</h1>

          {/* Author Header */}
          {book.author && typeof book.author === "object" && (
            <div
              className={styles.authorHeader}
              onClick={() => setShowAuthorModal(true)}
            >
              <Avatar src={resolveAuthorAvatar(book.author.avatar)} size={55}>
                {book.author.name?.[0]}
              </Avatar>
              <div className={styles.authorInfo}>
                <div className={styles.authorName}>{book.author.name}</div>
                <div className={styles.authorRole}>Зохиолч</div>
              </div>
            </div>
          )}

          {/* Rating */}
          <div className={styles.ratingWrapper}>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={20}
                className={styles.star}
                fill={star <= Math.round(displayRatingAvg) ? "#ffa500" : "none"}
                stroke="#ffa500"
              />
            ))}
            <span className={styles.ratingText}>
              {displayRatingAvg.toFixed(1)}
            </span>
            <span className={styles.reviewCount}>
              ({displayRatingCount || 0} үнэлгээ)
            </span>
          </div>

          {/* Format Selector */}
          <div className={styles.formatSelector}>
            <button
              className={`${styles.formatBtn} ${selectedFormat === "delivery" ? styles.activeFormat : ""}`}
              onClick={() => setSelectedFormat("delivery")}
            >
              <Truck size={16} />
              <span>Хүргэлтээр</span>
            </button>

            <button
              className={`${styles.formatBtn} ${selectedFormat === "epub" ? styles.activeFormat : ""}`}
              onClick={() => setSelectedFormat("epub")}
              disabled={
                !book.price?.epub?.price || book.price?.epub?.price === 0
              }
            >
              <BookOpen size={16} />
              <span>E-Pub</span>
            </button>

            <button
              className={`${styles.formatBtn} ${selectedFormat === "audio" ? styles.activeFormat : ""}`}
              onClick={() => setSelectedFormat("audio")}
              disabled={
                !book.price?.audio?.price || book.price?.audio?.price === 0
              }
            >
              <Headphones size={16} />
              <span>Audio</span>
            </button>

            <button
              className={`${styles.formatBtn} ${selectedFormat === "other" ? styles.activeFormat : ""}`}
              onClick={() => setSelectedFormat("other")}
            >
              <LayoutGrid size={16} />
              <span>Бусад</span>
            </button>
          </div>

          {/* Pricing Section for Delivery */}
          {selectedFormat === "delivery" && (
            <div className={styles.priceSection}>
              <div className={styles.priceRow}>
                <div className={styles.priceInfo}>
                  <span className={styles.priceLabel}>Үндсэн үнэ</span>
                  <span className={styles.priceValue}>
                    {new Intl.NumberFormat().format(finalPrice)}₮
                    {(discount > 0 || deliveryPricing.quantityDiscountTotal > 0) && (
                      <span className={styles.oldPrice}>
                        {new Intl.NumberFormat().format(currentPrice)}₮
                      </span>
                    )}
                  </span>
                </div>
                {deliveryInfo && (
                  <span className={styles.deliveryInfo}>
                    <Truck size={14} />
                    {getDeliveryFeeText(deliveryInfo, deliveryPricing.finalTotal)}
                  </span>
                )}
              </div>
              <div className={styles.priceBreakdown}>
                <div className={styles.priceBreakdownRow}>
                  <span>Нийт дүн</span>
                  <span>{totalBeforeDiscounts.toLocaleString()}₮</span>
                </div>
                {totalDiscount > 0 && (
                  <div className={styles.priceBreakdownRow}>
                    <span>Хөнгөлөлт</span>
                    <span>-{totalDiscount.toLocaleString()}₮</span>
                  </div>
                )}
                <div className={styles.priceBreakdownTotal}>
                  <span>Төлөх дүн ({quantity}ш)</span>
                  <strong>{deliveryPricing.finalTotal.toLocaleString()}₮</strong>
                </div>
              </div>
              {deliveryPricing.quantityDiscountPercent > 0 && (
                <div className={styles.quantityDiscountActive}>
                  {deliveryPricing.appliedRule?.minQuantity}ш+ •{" "}
                  {deliveryPricing.quantityDiscountPercent}% хямдрал • Хэмнэлт -
                  {deliveryPricing.quantityDiscountTotal.toLocaleString()}₮
                </div>
              )}

              {deliveryPricing?.appliedRule && (
                <div className={styles.stockInfo}>
                  {deliveryPricing.appliedRule.minQuantity} дээш:{" "}
                  {deliveryPricing.appliedRule.discountPercent}% нэмэлт хямдрал
                  идэвхтэй
                </div>
              )}

              <div className={styles.actionRow}>
                <div className={styles.quantityControl}>
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus size={14} />
                  </button>
                  <span>{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    disabled={quantity >= stockQty}
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {storeSettings?.allowOrders !== false && (
                  <>
                    <button className={styles.buyNowBtn} onClick={handleBuyNow}>
                      Худалдаж авах
                    </button>

                    <button
                      className={styles.cartBtn}
                      onClick={handleAddToCart}
                    >
                      <ShoppingCart size={16} />
                      Сагсанд хийх
                    </button>
                  </>
                )}
              </div>

              {storeSettings?.allowOrders === false && (
                <div className={styles.ordersDisabled}>
                  Захиалга хийх боломжгүй байна.
                </div>
              )}

              {storeSettings?.showProductStock === true && (
                <div className={styles.stockInfo}>
                  {stockQty} ширхэг бэлэн байна
                </div>
              )}

              {hasFormatDetails && (
                <div className={styles.priceDetailsWrap}>
                  <div className={styles.priceDetailsTitle}>
                    Нэмэлт мэдээлэл
                  </div>
                  <SafeHTML
                    html={formatDetails}
                    className={styles.priceDetailsHtml}
                  />
                </div>
              )}
            </div>
          )}

          {/* E-Pub and Audio Section */}
          {(selectedFormat === "epub" || selectedFormat === "audio") && (
            <div className={styles.priceSection}>
              <div className={styles.priceRow}>
                <div className={styles.priceInfo}>
                  <span className={styles.priceLabel}>Үндсэн үнэ</span>
                  <span className={styles.priceValue}>
                    {new Intl.NumberFormat().format(finalPrice)}₮
                  </span>
                </div>
              </div>

              <div className={styles.appDownload}>
                <div className={styles.downloadButtons}>
                  <a
                    href={
                      book.price?.[selectedFormat]?.appstore ||
                      appInfoItem?.appstore ||
                      "https://apps.apple.com/mn/app/gerelt/id1234567890"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.appStoreBtn}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                      fill="currentColor"
                    >
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    <div>
                      <small>Download on the</small>
                      <strong>App Store</strong>
                    </div>
                  </a>

                  <a
                    href={
                      book.price?.[selectedFormat]?.googlestore ||
                      appInfoItem?.googleplay ||
                      "https://play.google.com/store/apps/details?id=mn.gerelt.app"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.playStoreBtn}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                      fill="currentColor"
                    >
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    <div>
                      <small>GET IT ON</small>
                      <strong>Google Play</strong>
                    </div>
                  </a>
                </div>
              </div>

              <div className={styles.digitalFooter}>
                <p>
                  Google Play болон App Store - оос Gerelt App-ыг татан{" "}
                  {selectedFormat === "epub" ? "E-Pub" : "Audio"} хувилбараар
                  уншиж боломжтой
                </p>
              </div>

              {hasFormatDetails && (
                <div className={styles.priceDetailsWrap}>
                  <div className={styles.priceDetailsTitle}>
                    Нэмэлт мэдээлэл
                  </div>
                  <SafeHTML
                    html={formatDetails}
                    className={styles.priceDetailsHtml}
                  />
                </div>
              )}
            </div>
          )}

          {/* Other Format - Bookstore Information */}
          {selectedFormat === "other" && (
            <div className={styles.priceSection}>
              <div className={`${styles.priceRow} ${styles.bookstoreHeader}`}>
                <h3>Борлуулагдаж буй цэгүүд</h3>
              </div>

              <div className={`row ${styles.bookstoreList}`}>
                {book.price?.other?.bookstore &&
                book.price.other.bookstore.length > 0 ? (
                  book.price.other.bookstore.map((store, index) => (
                    <div key={store._id || index} className="col-md-6 col-12">
                      <button
                        type="button"
                        className={styles.bookstoreCard}
                        onClick={() => handleOpenStore(store)}
                      >
                        <BookstoreLogo
                          logo={store.logo}
                          name={store.name || "Дэлгүүр"}
                        />
                        <div className={styles.bookstoreInfo}>
                          <h4>{store.name || "Номын дэлгүүр"}</h4>
                        </div>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyBookstores}>
                    <p>Одоогоор номын дэлгүүрийн мэдээлэл оруулаагүй байна.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs Section */}
      <div className={styles.tabsSection}>
        <div className={styles.tabHeader}>
          {TAB_ITEMS.map((item) => (
            <button
              key={item.key}
              className={`${styles.tabBtn} ${activeTab === item.key ? styles.activeTab : ""}`}
              onClick={() => setActiveTab(item.key)}
            >
              {item.label}
              {item.key === "reviews" && ` (${displayRatingCount || 0})`}
            </button>
          ))}
        </div>

        <div key={activeTab} className={styles.tabContent}>
          {activeTab === "details" && (
            <div className={styles.description}>
              <h3 className="mb-3">Номын тухай</h3>
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    book.description || book.intro || "Тайлбар байхгүй байна.",
                }}
              />
            </div>
          )}
          {activeTab === "reviews" && (
            <div className={styles.reviewsSection}>
              <div className={styles.reviewFormCard}>
                <div className={styles.reviewFormHeader}>
                  <h3>Сэтгэгдэл үлдээх</h3>
                </div>

                {!userId ? (
                  <div className={styles.loginPrompt}>
                    Сэтгэгдэл үлдээхийн тулд{" "}
                    <Link href="/auth/login">нэвтэрнэ үү</Link>.
                  </div>
                ) : (
                  <>
                    <div className={styles.ratingInput}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className={`${styles.ratingStar} ${reviewForm.rating >= star ? styles.ratingStarActive : ""}`}
                          onClick={() => handleReviewRatingSelect(star)}
                          aria-label={`${star} одтой үнэлгээ`}
                          disabled={hasUserRated}
                        >
                          <Star
                            size={24}
                            fill={
                              reviewForm.rating >= star ? "#fbbf24" : "none"
                            }
                            stroke={
                              reviewForm.rating >= star ? "#fbbf24" : "#d1d5db"
                            }
                            strokeWidth={2}
                          />
                        </button>
                      ))}
                    </div>

                    <textarea
                      className={styles.reviewTextarea}
                      rows={4}
                      placeholder="Сэтгэгдлээ энд бичнэ үү"
                      value={reviewForm.comment}
                      onChange={handleReviewCommentChange}
                    />

                    {reviewSubmitError && (
                      <div className={styles.reviewError}>
                        {reviewSubmitError}
                      </div>
                    )}

                    <button
                      className={styles.submitReviewBtn}
                      type="button"
                      onClick={handleSubmitReview}
                      disabled={reviewSubmitting}
                    >
                      {reviewSubmitting
                        ? "Нийтэлж байна..."
                        : "Сэтгэгдэл нийтлэх"}
                    </button>
                  </>
                )}
              </div>

              {reviewsError && (
                <div className={styles.reviewError}>{reviewsError}</div>
              )}

              <div className={styles.reviewList}>
                {reviewsLoading && reviews.length === 0 ? (
                  <div className={styles.emptyState}>Ачаалж байна...</div>
                ) : reviews.length === 0 ? (
                  <div className={styles.emptyState}>
                    Сэтгэгдэл одоогоор байхгүй байна.
                  </div>
                ) : (
                  reviews.map((review) => {
                    return renderReviewItem(review);
                  })
                )}
              </div>

              {reviewPagination.pages > reviewPage && (
                <button
                  type="button"
                  className={styles.reviewLoadMore}
                  onClick={handleLoadMoreReviews}
                  disabled={reviewsLoading}
                >
                  {reviewsLoading ? "Ачаалж байна..." : "Илүүг үзэх"}
                </button>
              )}
            </div>
          )}
          {activeTab === "chapters" && (
            <>
              {hasChapters ? (
                <div
                  className={`${styles.chapterListWrap} ${isChapterScrollEnd ? styles.chapterListEnd : ""}`}
                >
                  <div
                    className={styles.chapterList}
                    onScroll={handleChapterScroll}
                  >
                    <ul>
                      {chapterItems.map((chapter, idx) => (
                        <li key={`${chapter}-${idx}`}>
                          <span className={styles.chapterIndex}>
                            Бүлэг - {idx + 1}
                          </span>
                          <span className={styles.chapterTitle}>{chapter}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {chapterItems.length > 4 && !isChapterScrollEnd && (
                    <div className={styles.chapterScrollHint}>
                      Доош гүйлгэж бусад бүлгийг үзэх
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  Бүлгийн мэдээлэл оруулаагүй байна.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {detailUrl && (
        <ShareSection
          detailUrl={detailUrl}
          shareText={shareTitle || book.name || book.title || "Ном"}
        />
      )}

      {/* Quotes Section */}
      {book.quotes && book.quotes.length > 0 && (
        <QuoteCarousel quotes={book.quotes} />
      )}

      {videos.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Холбоотой видео</h2>
          </div>
          <div className={styles.carousel}>
            <Swiper
              spaceBetween={20}
              slidesPerView={3}
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 14 },
                576: { slidesPerView: 2, spaceBetween: 16 },
                992: { slidesPerView: 3, spaceBetween: 20 },
              }}
            >
              {videos.map((video) => (
                <SwiperSlide
                  key={video._id || video.id || video.url}
                  className={styles.carouselSlide}
                >
                  <YouTubeCard video={video} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {relatedNews.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Холбоотой нийтлэл</h2>
          </div>
          <div className={styles.carousel}>
            <Swiper
              spaceBetween={20}
              slidesPerView={4}
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 14 },
                576: { slidesPerView: 2, spaceBetween: 16 },
                992: { slidesPerView: 3, spaceBetween: 18 },
                1200: { slidesPerView: 4, spaceBetween: 20 },
              }}
            >
              {relatedNews.map((article) => (
                <SwiperSlide
                  key={article._id || article.slug}
                  className={styles.carouselSlide}
                >
                  <Link
                    href={`/news/${article.slug || article._id}`}
                    className={newsGridStyles.card}
                  >
                    <div className={newsGridStyles.imageWrapper}>
                      {article.pictures && article.pictures[0] ? (
                        <img
                          src={resolveCdnImage(article.pictures[0])}
                          alt={article.name || "Article"}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className={newsGridStyles.placeholder} />
                      )}
                    </div>
                    <div className={newsGridStyles.content}>
                      <h3 className={newsGridStyles.title}>{article.name}</h3>
                      {article.createdAt && (
                        <p className={newsGridStyles.publishDate}>
                          {formatDate(article.createdAt)}
                        </p>
                      )}
                      <p className={newsGridStyles.excerpt}>
                        {getExcerpt(
                          article.details ||
                            article.intro ||
                            article.description,
                          25,
                        )}
                      </p>
                      {article.authors && (
                        <div className={newsGridStyles.authorInfo}>
                          {article.authors.avatar && (
                            <img
                              src={resolveUrl(article.authors.avatar)}
                              alt={article.authors.name || "Author"}
                              width={24}
                              height={24}
                              className={newsGridStyles.authorAvatar}
                              loading="lazy"
                              decoding="async"
                            />
                          )}
                          <span className={newsGridStyles.authorName}>
                            {article.authors.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {/* Author Modal */}
      <UnifiedAuthorModal
        author={book.author}
        open={showAuthorModal}
        onClose={() => setShowAuthorModal(false)}
      />

      {showBookstoreModal && selectedStore && (
        <BookstoreModal
          store={selectedStore}
          onClose={() => setShowBookstoreModal(false)}
        />
      )}
    </div>
  );
}

const BookstoreLogo = ({ logo, name }) => {
  const [hasError, setHasError] = useState(false);

  return (
    <div className={styles.bookstoreIcon}>
      {logo && !hasError ? (
        <img
          src={resolveUrl(logo)}
          alt={name || "Дэлгүүр"}
          onError={() => setHasError(true)}
          className={styles.bookstoreLogoImage}
        />
      ) : (
        <Store size={24} strokeWidth={1.8} aria-hidden="true" />
      )}
    </div>
  );
};

const BookstoreModal = ({ store, onClose }) => {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}>
          ×
        </button>
        <div className={styles.modalHeader}>
          <BookstoreLogo logo={store.logo} name={store.name || "Дэлгүүр"} />
          <div className={styles.modalAuthorInfo}>
            <h2>{store.name || "Номын дэлгүүр"}</h2>
          </div>
        </div>
        <div className={styles.modalBody}>
          {store.details ? (
            <div dangerouslySetInnerHTML={{ __html: store.details }} />
          ) : (
            <p>Дэлгэрэнгүй мэдээлэл байхгүй байна.</p>
          )}
          {store.link && (
            <a
              href={store.link}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.bookstoreLink}
            >
              Вэбсайт үзэх
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const QuoteCarousel = ({ quotes }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = () => setCurrentIndex((prev) => (prev + 1) % quotes.length);
  const prev = () =>
    setCurrentIndex((prev) => (prev - 1 + quotes.length) % quotes.length);

  const quote = quotes[currentIndex];
  const quoteSource =
    quote?.page !== undefined && quote?.page !== null
      ? String(quote.page).trim()
      : "";

  return (
    <div className={styles.quoteSection}>
      <button className={styles.navBtn} onClick={prev}>
        <ChevronLeft />
      </button>

      <div className={styles.quoteContent}>
        <div className={styles.quoteIcon}>“</div>
        <p className={styles.quoteText}>{quote.quote}</p>
        {quoteSource && <div className={styles.quotePage}>{quoteSource}</div>}

        <div className={styles.dots}>
          {quotes.map((_, idx) => (
            <span
              key={idx}
              className={`${styles.dot} ${idx === currentIndex ? styles.activeDot : ""}`}
            />
          ))}
        </div>
      </div>

      <button className={styles.navBtn} onClick={next}>
        <ChevronRight />
      </button>
    </div>
  );
};

function resolveUrl(path) {
  if (!path) return "/assets/img/placeholder.png";
  if (/^https?:\/\//i.test(path)) return path;
  const cdnRoot = String(base.cdnUrl || "").replace(/\/+$/, "");
  const normalized = String(path).replace(/^\/+/, "");
  return `${cdnRoot}/${normalized}`;
}

function resolveAuthorAvatar(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const cdnRoot = String(base.cdnUrl || "").replace(/\/+$/, "");
  const normalized = String(path).replace(/^\/+/, "");
  return `${cdnRoot}/150x150/${normalized}`;
}

function appendReplyToTree(items, parentId, reply) {
  return items.map((item) => {
    const itemId = item?._id || item?.id;
    if (itemId === parentId) {
      const nextReplies = Array.isArray(item.replies) ? [...item.replies] : [];
      nextReplies.push(reply);
      return { ...item, replies: nextReplies };
    }

    if (Array.isArray(item.replies) && item.replies.length > 0) {
      return {
        ...item,
        replies: appendReplyToTree(item.replies, parentId, reply),
      };
    }

    return item;
  });
}

function removeReviewFromTree(items, reviewId) {
  return items
    .filter((item) => (item?._id || item?.id) !== reviewId)
    .map((item) => ({
      ...item,
      replies: Array.isArray(item.replies)
        ? removeReviewFromTree(item.replies, reviewId)
        : [],
    }));
}

function resolveCdnImage(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = String(path).replace(/^\/+/, "");
  const cdnRoot = String(base.cdnUrl || "").replace(/\/+$/, "");
  return `${cdnRoot}/450/${normalized}`;
}

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

function truncateWords(text, maxWords) {
  if (!text) return "";
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(" ") + "...";
}

function getExcerpt(html, maxWords = 30) {
  const text = stripHtml(html);
  return truncateWords(text, maxWords);
}
